<script>
    import { hydrateInmate } from '../../inmate.js';
    import { formatDate } from '../../date-time.js';
    import { sortByProperty } from '../../sort.js';
    import { referenceDataRegimeLevelList, referenceDataCellStatusList} from "../../reference-data.js";
    import { vptServer, token, inmateDrawerOpen, getTooltipDisplayOptions } from '../../stores.js';

    import TableHead from '../../table/TableHead.svelte';
    import TableData from '../../table/TableData.svelte';
    import LocationData from '../../table/LocationData.svelte';

    export let id; 
    export let todaysConfig;

    let history = [];
    let searchCompleted = false;
    let sites = [];
    let keyworkers = [];
    let sortProperty = "date";
    let reverseSorted = true;

    $: {
        if (todaysConfig) {
            history = null;
            getEarlyDaysHistory();
        }
    }

    $: sortedHistory = history.sort(sortByProperty(sortProperty, reverseSorted));

    async function getEarlyDaysHistory() {
        searchCompleted = false;
        history = [];
        
        await fetch(`${$vptServer}/inmate/${id}/earlydays/list`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            data.forEach((i,index,arr) => {
                arr[index] = {...i,
                    ...hydrateInmate(i, i.indicatorTrend, null, $referenceDataRegimeLevelList, $referenceDataCellStatusList, null, null)};
            });
            history = data;
        })
        .catch(error => {
            console.error("Error getting early days history");
            console.error(error);
            return;
        });

        keyworkers = [];
        history.forEach((h) => {
            if (h.keyworkerName && !(keyworkers.some(e => e.label === h.keyworkerName))) {
                let keyworker = {value : h.keyworkerName, label : h.keyworkerName}
                keyworkers = [...keyworkers, keyworker];
            }
        });
        keyworkers = keyworkers.sort(sortByProperty("label", false));

        if (todaysConfig.showSiteName) {
            sites = [];
            history.forEach((history) => {
                if (history.siteUrn && !(sites.some(e => e.label === history.siteName))) {
                    let site = {value: history.siteUrn, label: history.siteName}
                    sites = [...sites, site];
                }
            });
            sites = sites.sort(sortByProperty("label", false));
        }

        searchCompleted = true;
    }

    function sort(newSortProperty, reverse) {
        if (newSortProperty === sortProperty) {
            reverseSorted = !reverseSorted;
        }
        else {
            sortProperty = newSortProperty;
            reverseSorted = reverse ? true : false;
        }
    }
</script>

<style>
    .container-fluid {
        padding-top: 168px;
        min-width: 1100px;
    }

    .container-fluid {
        padding-right: 96px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }

    .container-fluid-short {
        padding-right: 270px;
        transition: 0.4s ease-out;
        transition-delay: 0.2s;
    }

    .table.table-card thead {
        top: 213px;
    }
</style>

<div class="container-fluid" class:container-fluid-short={$inmateDrawerOpen}>
    <div class="row">
        <div class="col-sm-12">
            {#if searchCompleted}
                {#if sortedHistory.length > 0}
                    <table class="table table-sm table-borderless table-card">
                        <thead>
                            <tr>
                                <TableHead on:click={() => sort("indicatorTargeted", true)} propertyName={"indicatorTargeted"} width={'xs'}
                                        tooltipId={"target-header"} tooltipText={`${todaysConfig.inmateLabelName} is on the Targeted list`} 
                                        {sortProperty} {reverseSorted}>
                                    <img id="target-header" src="/assets/header/target.svg" alt="{todaysConfig.inmateLabelName} is on the Targeted list(s)" />
                                </TableHead>

                                <TableHead on:click={() => sort("trendSort")} propertyName={"trendSort"} icon={"graph-up"}
                                    tooltipText={"Daily Change"} tooltipId={"trend-header"}
                                    {sortProperty} {reverseSorted} invertArrow={true} />

                                <TableHead on:click={() => sort("date", true)} propertyName={"date"} title={"Date"}
                                    {sortProperty} {reverseSorted} />
                                    
                                {#if todaysConfig.showCellStatusIcon}
                                    <TableHead on:click={() => sort("cellStatusSort")} propertyName={"cellStatusSort"} icon={"file-person"}
                                        tooltipText="Cell Status" tooltipId={"cell-status-header"}
                                        {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                <TableHead on:click={() => sort("wingCellSort")} propertyName={"wingCellSort"} title={"Cell"}
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("indicatorTemporaryDischarged", true)} propertyName={"indicatorTemporaryDischarged"} 
                                    icon={"building-fill"} tooltipText={"Temporary Discharge"} tooltipId={"temp-discharge-header"} 
                                    {sortProperty} {reverseSorted} />

                                {#if todaysConfig.earlyDaysVisit}
                                    <TableHead on:click={() => sort("indicatorVisit", true)} propertyName={"indicatorVisit"} 
                                        title={todaysConfig.isolationVisitLabel} width={'md'} center={true}
                                        tooltipText={(todaysConfig.isolationVisitDescription && !(todaysConfig.isolationVisitLabel == todaysConfig.isolationVisitDescription)) ? todaysConfig.isolationVisitDescription : null}
                                        tooltipId={"visits-header"} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if todaysConfig.earlyDaysWork}
                                    <TableHead on:click={() => sort("indicatorWork", true)} propertyName={"indicatorWork"} 
                                        title={todaysConfig.isolationWorkLabel} width={'md'} center={true}
                                        tooltipText={(todaysConfig.isolationWorkDescription && !(todaysConfig.isolationWorkLabel == todaysConfig.isolationWorkDescription)) ? todaysConfig.isolationWorkDescription : null}
                                        tooltipId={"work-header"} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if todaysConfig.earlyDaysPhone}
                                    <TableHead on:click={() => sort("indicatorPhone", true)} propertyName={"indicatorPhone"} 
                                        title={todaysConfig.isolationPhoneLabel} width={'md'} center={true}
                                        tooltipText={(todaysConfig.isolationPhoneDescription && !(todaysConfig.isolationPhoneLabel == todaysConfig.isolationPhoneDescription)) ? todaysConfig.isolationPhoneDescription : null}
                                        tooltipId={"phone-header"} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if todaysConfig.earlyDaysMeal}
                                    <TableHead on:click={() => sort("indicatorMeal", true)} propertyName={"indicatorMeal"} 
                                        title={todaysConfig.isolationMealLabel} width={'md'} center={true}
                                        tooltipText={(todaysConfig.isolationMealDescription && !(todaysConfig.isolationMealLabel == todaysConfig.isolationMealDescription)) ? todaysConfig.isolationMealDescription : null}
                                        tooltipId={"meals-header"} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if todaysConfig.earlyDaysCanteen}
                                    <TableHead on:click={() => sort("indicatorCanteen", true)} propertyName={"indicatorCanteen"} 
                                        title={todaysConfig.isolationCanteenLabel} width={'md'} center={true}
                                        tooltipText={(todaysConfig.isolationCanteenDescription && !(todaysConfig.isolationCanteenLabel == todaysConfig.isolationCanteenDescription)) ? todaysConfig.isolationCanteenDescription : null}
                                        tooltipId={"canteen-header"} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if todaysConfig.earlyDaysGym}
                                    <TableHead on:click={() => sort("indicatorGym", true)} propertyName={"indicatorGym"} 
                                        title={todaysConfig.isolationGymLabel} width={'md'} center={true}
                                        tooltipText={(todaysConfig.isolationGymDescription && !(todaysConfig.isolationGymLabel == todaysConfig.isolationGymDescription)) ? todaysConfig.isolationGymDescription : null}
                                        tooltipId={"gym-header"} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if todaysConfig.earlyDaysPa}
                                    <TableHead on:click={() => sort("indicatorActivity", true)} propertyName={"indicatorActivity"} 
                                        title={todaysConfig.isolationPaLabel} width={'md'} center={true}
                                        tooltipText={(todaysConfig.isolationPaDescription && !(todaysConfig.isolationPaLabel == todaysConfig.isolationPaDescription)) ? todaysConfig.isolationPaDescription : null}
                                        tooltipId={"activities-header"} {sortProperty} {reverseSorted} />
                                {/if}

                                <TableHead on:click={() => sort("regimeLevelDescription")} propertyName={"regimeLevelDescription"} 
                                    title={"Regime"} {sortProperty} {reverseSorted} classes={"table-pad-left"} />

                                {#if keyworkers.length > 0}
                                    <TableHead on:click={() => sort("keyworkerName")} propertyName={"keyworkerName"} 
                                        title={todaysConfig.keyworkerLabelName} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if todaysConfig.showSiteName && sites.length > 1}
                                    <TableHead on:click={() => sort("siteName")} propertyName={"siteName"}
                                        title={"Site"} {sortProperty} {reverseSorted} classes="hidden-col-lv-1" />
                                {/if}
                            </tr>
                        </thead>

                        <tbody>
                            {#each sortedHistory as history (history.vptHeaderUrn)}
                                <tr>
                                    <!-- Targeted Indicator -->
                                    <TableData display={history.indicatorTargeted} classes="table-text-center" tooltipId={`targeted-indicator-${history.vptHeaderUrn}`}
                                            tooltipText={`${todaysConfig.inmateLabelName} was targeted on this date`}>
                                        <img id="targeted-indicator-{history.vptHeaderUrn}" src="/assets/target-indicator.svg" alt="{todaysConfig.inmateLabelName} was targeted on this date" />    
                                    </TableData>

                                    <!-- Trend Indicator -->
                                    <TableData display={history.indicatorTrend == 'U'} icon={"arrow-up-short"} iconSize={'xl'}
                                        tooltipText={"Engagement has increased since the previous day"} 
                                        tooltipId={`trend-indicator-${history.vptHeaderUrn}`} classes={"text-success"}/>

                                    <!-- Date -->
                                    <TableData text={formatDate(history.date)} />

                                    <!-- Cell Status -->
                                    {#if todaysConfig.showCellStatusIcon}
                                        <TableData display={history.vptCellStatusUrn > 0} tooltipId={`cell-status-data-${history.vptHeaderUrn}`} 
                                                tooltipText={history.cellStatusDescription} center={true}>
                                            <img id="cell-status-data-{history.vptHeaderUrn}" src="/assets/{history.cellStatusIcon}.svg" alt="{history.cellStatusDescription}" />
                                        </TableData>
                                    {/if}

                                    <!-- Cell Location -->
                                    <LocationData inmateUrn={history.vptHeaderUrn} unit={history.unit} 
                                        wing={history.wing} cell={history.cell} />

                                    <!-- Temporary Discharge Indicator -->
                                    <TableData display={history.indicatorTemporaryDischarged} icon={"building-fill-x"}
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions({ maxWidth: 500 })}
                                        tooltipText={{tooltipLines: [history.dischargedTo]}} />

                                    <!-- Visits Engagement -->
                                    {#if todaysConfig.earlyDaysVisit}
                                        <TableData icon={history.indicatorVisit ? "check-circle" : "dash-circle"} iconSize={'lg'}
                                            classes={history.indicatorVisit ? "text-success" : "text-disabled"}
                                            tooltipId={`visit-data-${history.vptHeaderUrn}`}
                                            tooltipText={history.indicatorVisit ? "Visited" : null} />
                                    {/if}

                                    <!-- Work Engagement -->
                                    {#if todaysConfig.earlyDaysWork}
                                        <TableData icon={history.indicatorWork ? "check-circle" : "dash-circle"} iconSize={'lg'}
                                            classes={history.indicatorWork ? "text-success" : "text-disabled"}
                                            tooltipId={`work-data-${history.vptHeaderUrn}`}
                                            tooltipText={history.indicatorWork ? "Attended" : null} />
                                    {/if}

                                    <!-- Phone Engagement -->
                                    {#if todaysConfig.earlyDaysPhone}
                                        <TableData icon={history.indicatorPhone ? "check-circle" : "dash-circle"} iconSize={'lg'}
                                            classes={history.indicatorPhone ? "text-success" : "text-disabled"}
                                            tooltipId={`phone-data-${history.vptHeaderUrn}`}
                                            tooltipText={history.indicatorPhone ? "Topped Up" : null} />
                                    {/if}

                                    <!-- Meals Engagement -->
                                    {#if todaysConfig.earlyDaysMeal}
                                        <TableData icon={history.indicatorMeal ? "check-circle" : "dash-circle"} iconSize={'lg'}
                                            classes={history.indicatorMeal ? "text-success" : "text-disabled"}
                                            tooltipId={`meal-data-${history.vptHeaderUrn}`}
                                            tooltipText={history.indicatorMeal ? "Selected" : null} />
                                    {/if}

                                    <!-- Canteen Engagement -->
                                    {#if todaysConfig.earlyDaysCanteen}
                                        <TableData icon={history.indicatorCanteen ? "check-circle" : "dash-circle"} iconSize={'lg'}
                                            classes={history.indicatorCanteen ? "text-success" : "text-disabled"}
                                            tooltipId={`canteen-data-${history.vptHeaderUrn}`}
                                            tooltipText={history.indicatorCanteen ? "Purchased" : null} />
                                    {/if}
                                    
                                    <!-- Gym Engagement -->
                                    {#if todaysConfig.earlyDaysGym}
                                        <TableData icon={history.indicatorGym ? "check-circle" : "dash-circle"} iconSize={'lg'}
                                            classes={history.indicatorGym ? "text-success" : "text-disabled"}
                                            tooltipId={`gym-data-${history.vptHeaderUrn}`}
                                            tooltipText={history.indicatorGym ? "Attended" : null} />
                                    {/if}
                                    
                                    <!-- PA Engagement -->
                                    {#if todaysConfig.earlyDaysPa}
                                        <TableData icon={history.indicatorActivity ? "check-circle" : "dash-circle"} iconSize={'lg'}
                                            classes={history.indicatorActivity ? "text-success" : "text-disabled"}
                                            tooltipId={`pa-data-${history.vptHeaderUrn}`}
                                            tooltipText={history.indicatorActivity ? "Attended" : null} />
                                    {/if}

                                    <!-- Regime -->
                                    <TableData display={history.regimeLevelDescription} text={history.regimeLevelDescription}
                                        classes="table-pad-left" tooltipId={`regime-data-${history.vptHeaderUrn}`}
                                        tooltipText={history.regimeStartDate ? `Since ${formatDate(history.regimeStartDate)}` : null} />

                                    <!-- Keyworker -->
                                    {#if keyworkers.length > 0}
                                        <TableData display={history.keyworkerName} text={history.keyworkerName} tooltipId={`keyworker-data-${history.vptHeaderUrn}`}
                                            tooltipText={history.keyworkerStartDate ? `Since ${formatDate(history.keyworkerStartDate)}` : null} />
                                    {/if}

                                    <!-- Site -->
                                    {#if todaysConfig.showSiteName && sites.length > 1}
                                        <TableData display={history.siteName} text={history.siteName} classes="hidden-col-lv-1" />
                                    {/if}
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                {:else}
                    <div class="alert alert-danger" role="alert">No {todaysConfig ? todaysConfig.earlyDaysLabelName : ''} data was found.</div>
                {/if}
            {:else}
                <div class="spinner-container">
                    <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
                    <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
                </div>
            {/if}
        </div>
    </div>
</div>
